import React, { useCallback, useEffect, useState } from 'react'
import { Text, Popover, IconButton, Flex } from '@moonpig/launchpad-components'
import { styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import {
  IconSystemSearchSimilar,
  IconSystemCrossSmall,
} from '@moonpig/launchpad-assets'
import { spacingPx } from '@moonpig/launchpad-theme'
import {
  CookieCategory,
  getBrowserCookies,
  setBrowserCookie,
  useOneTrustConsentCategory,
} from '@moonpig/web-core-browser-storage'
import { useLocaleText } from '../ProductTile/ProductTile.locale'

const ICON_SIZE = spacingPx(8)
type Props = {
  isfirstProductTile?: boolean
}

const StyledButton = styled(IconButton)`
  position: absolute;
  bottom: 8px;
  left: 8px;
  z-index: 2;
  max-width: 40px;
  max-height: 40px;
  background: #ffffff;
  border: 1px solid rgb(232, 232, 232);
  border-radius: 40px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f0f1f3;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
`

const StyledSearchSimilarIcon = styled(IconSystemSearchSimilar)`
  ${s({ maxHeight: '19px' })}
`
const StyledDiv = styled(Flex)`
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  max-width: 228px;
`
const StyledContentGroup = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  width: 100%;
`
const StyledCloseButton = styled(IconButton)`
  width: ${ICON_SIZE};
  height: ${ICON_SIZE};
  display: flex;
  align-items: center;
  justify-content: center;
  ${s({
    ml: 4,
    color: 'colorInteractionControlHoverFill',
  })};
`
export const MoreLikeThisButton = ({ isfirstProductTile }: Props) => {
  const localiseText = useLocaleText()
  const [isOpen, setIsOpen] = useState(false)
  const oneTrustCookiesEnabled = useOneTrustConsentCategory(
    CookieCategory.FUNCTIONAL,
  )
  const cookieName = 'mnpg_more_like_this'
  const cookieMaxAge = 60 * 60 * 24 * 365 // 12 months
  const cookieValue = 'true'

  const labels = {
    popoverTitle: localiseText('card_popover_header.more_like_this'),
    popoverContent: localiseText('card_popover.more_like_this'),
    popoverLabel: localiseText('card_popover.more_like_this'),
  }

  const hasSeenPopover = useCallback(() => {
    const cookies = getBrowserCookies()
    return cookies?.[cookieName] === 'true'
  }, [cookieName])

  useEffect(() => {
    if (!isfirstProductTile) return
    const delayedTimer = setTimeout(() => {
      if (!hasSeenPopover()) {
        setIsOpen(true)
      }
    }, 1000)
    return () => {
      clearTimeout(delayedTimer)
    }
  }, [isfirstProductTile, hasSeenPopover])

  const handleClose = () => {
    setIsOpen(false)
    if (oneTrustCookiesEnabled) {
      setBrowserCookie(
        cookieName,
        cookieValue,
        { maxAge: cookieMaxAge, path: '/' },
        CookieCategory.STRICTLY_NECESSARY,
      )
    }
  }

  return (
    <>
      <Popover
        isOpen={isOpen}
        onDismiss={handleClose}
        align="center"
        side="bottom"
        bgcolor="colorPrimary02"
      >
        <Popover.Trigger>
          <StyledButton
            label={labels.popoverLabel}
            aria-label={labels.popoverLabel}
            icon={IconSystemSearchSimilar}
            tabIndex={0}
          >
            <StyledSearchSimilarIcon />
          </StyledButton>
        </Popover.Trigger>
        <Popover.Content>
          <StyledDiv>
            <StyledContentGroup>
              <Text typography="typeBodyLabel" color="colorTextInverted">
                {labels.popoverTitle}
              </Text>
              <StyledCloseButton
                label="Close text box"
                icon={IconSystemCrossSmall}
                onClick={handleClose}
                tabIndex={-1}
              />
            </StyledContentGroup>
            <Text
              typography="typeBodyText"
              color="colorTextInverted"
              pt={2}
              pb={4}
            >
              {labels.popoverContent}
            </Text>
          </StyledDiv>
        </Popover.Content>
      </Popover>
    </>
  )
}
